import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@app/core/notifications';
import {
  useFragmentsDeliveryAPIClientEnableDelivery,
  useFragmentsDeliveryAPIClientGetDeliveryStatus,
} from '@mi-front/api-clients';
import { GetFragmentDto } from '@mi-front/core';
import { Spinner } from '@mi-front/ui';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, FormContainer } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { StationsInputMui } from '../../../../../../../../components/stations/StationsInputMui';
import { useDeliveryEnabledStations } from './useDeliveryEnabledStations';

interface Props {
  fragment: GetFragmentDto;
  onStarted: () => void;
}

const schema = z.object({
  targetStation: z
    .object({
      id: z.string(),
    })
    .nullable(),
});

type FormData = z.infer<typeof schema>;

export function DeliveryStart({ fragment, onStarted }: Props) {
  const { formatMessage, $t } = useIntl();
  const { notifyInfo, notifyError } = useToast();
  const queryClient = useQueryClient();
  const stations = useDeliveryEnabledStations();

  const deliveryStatusQueryKey = useFragmentsDeliveryAPIClientGetDeliveryStatus(
    fragment.id,
  ).queryKey;

  const { mutate: enableDelivery, isPending } = useFragmentsDeliveryAPIClientEnableDelivery({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: deliveryStatusQueryKey,
        });
        notifyInfo(
          $t({
            id: 'fragments.delivery.exportStarted',
            defaultMessage: 'Export iniciado',
          }),
        );
        onStarted();
      },
      onError: (error: any) => {
        notifyError(
          $t(
            {
              id: 'fragments.delivery.exportStartError',
              defaultMessage:
                'Ocurrió un error al iniciar el export, contacte a soporte (status: {status})',
            },
            { status: error.status },
          ),
        );
        console.log(error);
      },
    },
  });

  const onSubmit = (data: FormData) => {
    enableDelivery({
      fragmentId: fragment.id,
      data: { targetStationId: data.targetStation?.id },
    });
  };

  if (!stations) {
    return <Spinner />;
  }

  if (!stations.length) {
    return null;
  }

  return (
    <Box>
      <FormContainer
        resolver={zodResolver(schema)}
        values={{
          targetStation: stations.find((s) => s.id === fragment.stationId) || null,
        }}
        onSuccess={onSubmit}
      >
        <Box sx={{ maxWidth: 300 }} mb={1}>
          <Controller
            name="targetStation"
            render={({ field }) => {
              return (
                <StationsInputMui
                  stations={stations}
                  value={field.value}
                  onChange={field.onChange}
                  selectProps={{
                    label: $t({
                      id: 'fragments.delivery.destinationStation',
                      defaultMessage: 'Emisora destino',
                    }),
                    size: 'small',
                  }}
                />
              );
            }}
          />
        </Box>
        <Button
          type="submit"
          color="inherit"
          variant="outlined"
          loading={isPending}
          startIcon={<FileUploadIcon />}
        >
          {formatMessage({
            id: 'fragments.deliveryStatus.start',
            defaultMessage: 'Iniciar export externo',
          })}
        </Button>
      </FormContainer>
    </Box>
  );
}
